import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useMutation, useQuery } from "react-query";
import { authContext } from "./auth-context";
import ProductsService from "../services/productsService";
import { SyncProductsStatusModal } from "../pages/Products/components/SyncProductsStatusModal";
import { toast } from "react-toastify";

type Props = {
  children: ReactNode;
};

const syncProductsContext = createContext<{
  isRequestingSync: boolean;
  syncProducts: () => void;
}>({
  isRequestingSync: false,
  syncProducts: () => {},
});

export const useSyncProducts = () => useContext(syncProductsContext);

const SyncProductsProvider = ({ children }: Props) => {
  const { token } = useContext(authContext);
  const [isStartSyncCheck, setStartSyncCheck] = useState<boolean>(false);

  const { data: syncProductsStatus } = useQuery({
    queryKey: ["syncProductsStatus"],
    queryFn: () => {
      return ProductsService.syncProductsStatus(token ?? "");
    },
    refetchInterval: isStartSyncCheck ? 3000 : false,
    onSuccess: ({ is_syncing }: { is_syncing: boolean }) => {
      if (isStartSyncCheck && !is_syncing) {
        toast.success("Ο συγχρονισμός των προιόντων ολοκληρώθηκε επιτυχώς");
      }
      if (!is_syncing) {
        setStartSyncCheck(false);
      }
    },
    onError: () => {
      setStartSyncCheck(false);

      toast.error("Ο συγχρονισμός των προιόντων απέτυχε");
    },
    enabled: !!token,
  });

  const { mutate: syncProducts, isLoading: isRequestingSync } = useMutation({
    mutationFn: () => {
      return ProductsService.syncProducts(token ?? "", setStartSyncCheck);
    },
  });

  const value = useMemo(
    () => ({
      isRequestingSync,
      syncProducts,
    }),
    [isRequestingSync, syncProducts],
  );

  useEffect(() => {
    if (syncProductsStatus && syncProductsStatus?.is_syncing) {
      setStartSyncCheck(true);
    }
  }, [syncProductsStatus]);

  return (
    <syncProductsContext.Provider value={value}>
      <SyncProductsStatusModal showModal={isStartSyncCheck} />
      {children}
    </syncProductsContext.Provider>
  );
};

export default SyncProductsProvider;
